import React, { useState } from 'react';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { projectTypes, usageTypes, budgetRanges } from '../../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    intendedUsage: '',
    deadline: '',
    budgetRange: '',
    message: ''
  });
  
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          intendedUsage: '',
          deadline: '',
          budgetRange: '',
          message: ''
        });
        if (onSuccess) onSuccess();
      } else {
        setStatus('error');
        setErrorMessage(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  const inputClasses = "w-full bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-4 py-3 text-white placeholder-[#505050] focus:outline-none focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/20 transition-colors";
  const labelClasses = "block text-[#B8B8B8] text-sm font-medium mb-2";
  const selectClasses = "w-full bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/20 transition-colors appearance-none cursor-pointer";

  if (status === 'success') {
    return (
      <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-[#C9A962]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-[#C9A962]" />
        </div>
        <h3 className="heading-2 mb-4">Message Sent Successfully!</h3>
        <p className="body-medium text-[#B8B8B8] mb-6">
          Thank you for reaching out. Joy will review your project details and get back to you within 24-48 hours.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2A2A] text-white rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name & Email Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className={labelClasses}>
            Full Name <span className="text-[#C9A962]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your full name"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="email" className={labelClasses}>
            Email Address <span className="text-[#C9A962]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            className={inputClasses}
          />
        </div>
      </div>

      {/* Company */}
      <div>
        <label htmlFor="company" className={labelClasses}>
          Company / Organization
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Your company name (optional)"
          className={inputClasses}
        />
      </div>

      {/* Project Type & Intended Usage Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="projectType" className={labelClasses}>
            Project Type <span className="text-[#C9A962]">*</span>
          </label>
          <div className="relative">
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              required
              className={selectClasses}
            >
              <option value="" disabled>Select project type</option>
              {projectTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-[#505050]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="intendedUsage" className={labelClasses}>
            Intended Usage <span className="text-[#C9A962]">*</span>
          </label>
          <div className="relative">
            <select
              id="intendedUsage"
              name="intendedUsage"
              value={formData.intendedUsage}
              onChange={handleChange}
              required
              className={selectClasses}
            >
              <option value="" disabled>Select intended usage</option>
              {usageTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-[#505050]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Deadline & Budget Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="deadline" className={labelClasses}>
            Project Deadline <span className="text-[#C9A962]">*</span>
          </label>
          <input
            type="date"
            id="deadline"
            name="deadline"
            value={formData.deadline}
            onChange={handleChange}
            required
            min={new Date().toISOString().split('T')[0]}
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="budgetRange" className={labelClasses}>
            Budget Range <span className="text-[#C9A962]">*</span>
          </label>
          <div className="relative">
            <select
              id="budgetRange"
              name="budgetRange"
              value={formData.budgetRange}
              onChange={handleChange}
              required
              className={selectClasses}
            >
              <option value="" disabled>Select budget range</option>
              {budgetRanges.map((range) => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-[#505050]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className={labelClasses}>
          Project Details <span className="text-[#C9A962]">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          placeholder="Tell us about your project: script length, tone requirements, target audience, and any other relevant details..."
          className={`${inputClasses} resize-none`}
        />
      </div>

      {/* Error Message */}
      {status === 'error' && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#C9A962] text-[#0D0D0D] font-medium rounded-full hover:bg-[#E5D4A1] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'loading' ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send size={20} />
            Send Message
          </>
        )}
      </button>

      <p className="text-center text-[#505050] text-sm">
        By submitting this form, you agree to our terms and privacy policy.
      </p>
    </form>
  );
};

export default ContactForm;
